import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with JWT token
const createAuthAxios = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const subscriptionService = {
  // ============================================
  // PLAN RETRIEVAL METHODS
  // ============================================
  
  // Get available subscription plans (no auth required)
  // Returns array of plans for backward compatibility
  getPlans: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/subscription/plans`);
      // API returns { plans: [...], footnotes: [...], trialDays: 14 }
      // Return just the plans array for backward compatibility
      return response.data.plans || response.data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  },

  // Get full plans response including footnotes and trial info
  getPlansWithMeta: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/subscription/plans`);
      return response.data; // Returns full { plans, footnotes, trialDays }
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  },

  // ============================================
  // SUBSCRIPTION STATUS & MANAGEMENT
  // ============================================

  // Get current subscription status (requires auth)
  getStatus: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get('/subscription/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      throw error;
    }
  },

  // Get feature access summary (requires auth)
  getFeatureAccess: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get('/subscription/features');
      return response.data;
    } catch (error) {
      console.error('Error fetching feature access:', error);
      throw error;
    }
  },

  // Check access to a specific feature (requires auth)
  checkFeatureAccess: async (featureKey) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(`/subscription/features/${featureKey}`);
      return response.data;
    } catch (error) {
      console.error('Error checking feature access:', error);
      throw error;
    }
  },

  // Get team limits (requires auth)
  getTeamLimits: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get('/subscription/team-limits');
      return response.data;
    } catch (error) {
      console.error('Error fetching team limits:', error);
      throw error;
    }
  },

  // ============================================
  // CHECKOUT & PAYMENT METHODS
  // ============================================

  // Create Stripe Checkout session (requires auth)
  // UPDATED: Added optional promotionCode parameter
  createCheckoutSession: async (planName, billingPeriod, promotionCode = null) => {
    try {
      const authAxios = createAuthAxios();
      
      const payload = {
        PlanName: planName,
        BillingPeriod: billingPeriod
      };
      
      // Add promotion code if provided
      if (promotionCode && promotionCode.trim()) {
        payload.PromotionCode = promotionCode.trim();
      }
      
      const response = await authAxios.post('/subscription/create-checkout-session', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },

  // Create Stripe Customer Portal session (requires auth)
  createPortalSession: async (returnUrl) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post('/subscription/create-portal-session', {
        returnUrl
      });
      return response.data;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  },

  // ============================================
  // PLAN MANAGEMENT METHODS
  // ============================================

  // Change subscription plan (upgrade/downgrade or billing period change)
  changePlan: async (newPlanName, billingPeriod) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post('/subscription/change-plan', {
        NewPlanName: newPlanName,
        BillingPeriod: billingPeriod
      });
      return response.data;
    } catch (error) {
      console.error('Error changing plan:', error);
      throw error;
    }
  },

  // Cancel subscription
  cancelSubscription: async (cancelImmediately, cancellationReason) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post('/subscription/cancel', {
        CancelImmediately: cancelImmediately,
        CancellationReason: cancellationReason
      });
      return response.data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  // ============================================
  // BILLING METHODS
  // ============================================

  // Get billing history
  getBillingHistory: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get('/subscription/billing-history');
      return response.data;
    } catch (error) {
      console.error('Error fetching billing history:', error);
      throw error;
    }
  },

  // Get current payment method
  getPaymentMethod: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get('/subscription/payment-method');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment method:', error);
      throw error;
    }
  },

  // Create SetupIntent for updating payment method
  createSetupIntent: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post('/subscription/create-setup-intent');
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error;
    }
  },

  // Update payment method
  updatePaymentMethod: async (paymentMethodId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post('/subscription/update-payment-method', {
        PaymentMethodId: paymentMethodId
      });
      return response.data;
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    }
  },

  // ============================================
  // HELPER METHODS
  // ============================================

  // Helper to redirect to Stripe Checkout
  redirectToCheckout: (sessionUrl) => {
    window.location.href = sessionUrl;
  },

  // Helper to redirect to Stripe Customer Portal
  redirectToPortal: (portalUrl) => {
    window.location.href = portalUrl;
  }
};

export default subscriptionService;